import { sectionRenderer } from '@theme/section-renderer';

const RESULTS_SECTION_ID = 'shop-the-look-results';

class ShopTheLookPdp extends HTMLElement {
  #looks = [];
  #slides = [];
  #currentIndex = 0;
  #skuCache = new Map();
  #resizeTimeout = null;

  async connectedCallback() {
    const dataScript = this.querySelector('script[data-look-data]');
    if (!dataScript) return;

    try {
      this.#looks = JSON.parse(dataScript.textContent || '[]');
    } catch {
      this.#looks = [];
    }

    if (this.#looks.length === 0) {
      this.hidden = true;
      return;
    }

    this.trackEl = this.querySelector('[data-look-track]');
    this.viewportEl = this.querySelector('.shop-the-look-pdp__viewport');
    this.slideTemplate = this.querySelector('[data-look-slide-template]');
    this.prevBtn = this.querySelector('[data-look-prev]');
    this.nextBtn = this.querySelector('[data-look-next]');
    this.navEl = this.querySelector('.shop-the-look-pdp__nav');

    this.prevBtn?.addEventListener('click', () => this.#goTo(this.#currentIndex - 1));
    this.nextBtn?.addEventListener('click', () => this.#goTo(this.#currentIndex + 1));

    const resolvedLooks = await Promise.all(
      this.#looks.map(async (look) => {
        try {
          const result = await this.#getLookResults(look);
          return result.visibleCount > 0 ? { look, result } : null;
        } catch (error) {
          console.error('Shop The Look: failed to load look', look.id, error);
          return null;
        }
      })
    );

    const visible = resolvedLooks.filter(Boolean);

    if (visible.length === 0) {
      this.hidden = true;
      return;
    }

    this.#buildSlides(visible);
    this.#syncImageHeights();
    this.#syncViewportHeight();

    if (this.#slides.length <= 1 && this.navEl) {
      this.navEl.hidden = true;
    }

    this.#updateNavState();

    window.addEventListener('resize', () => {
      clearTimeout(this.#resizeTimeout);
      this.#resizeTimeout = setTimeout(() => {
        this.#syncImageHeights();
        this.#syncViewportHeight();
      }, 150);
    });
  }

  #syncViewportHeight() {
    if (!(this.viewportEl instanceof HTMLElement)) return;
    const current = this.#slides[this.#currentIndex];
    if (!current?.root) return;
    this.viewportEl.style.height = `${current.root.offsetHeight}px`;
  }

  #buildSlides(visible) {
    if (!this.trackEl || !(this.slideTemplate instanceof HTMLTemplateElement)) return;

    this.trackEl.innerHTML = '';
    this.#slides = visible.map(({ look, result }, index) => {
      const fragment = this.slideTemplate.content.cloneNode(true);
      const root = fragment.querySelector('.shop-the-look-pdp__slide');

      const imageEl = root?.querySelector('[data-look-image]');
      if (imageEl instanceof HTMLImageElement) {
        imageEl.src = look.image || '';
        imageEl.alt = look.imageAlt || '';
      }
      const imageWrap = root?.querySelector('[data-look-image-wrap]');
      if (imageWrap instanceof HTMLElement) {
        imageWrap.hidden = !look.image;
      }
      const productsEl = root?.querySelector('[data-look-products]');
      if (productsEl) {
        productsEl.innerHTML = result.html;
        productsEl.classList.toggle('shop-the-look-pdp__products--compact', result.visibleCount <= 4);
      }

      this.trackEl.appendChild(fragment);
      return { root, index };
    });
  }

  #syncImageHeights() {
    const isStacked = window.innerWidth <= 989;
    this.#slides.forEach(({ root }) => {
      const imageWrap = root?.querySelector('[data-look-image-wrap]');
      const productsEl = root?.querySelector('[data-look-products]');
      if (!(imageWrap instanceof HTMLElement) || !(productsEl instanceof HTMLElement)) return;
      imageWrap.style.height = !isStacked && productsEl.offsetHeight > 0 ? `${productsEl.offsetHeight}px` : '';
    });
  }

  #goTo(index) {
    if (index < 0 || index >= this.#slides.length) return;
    this.#currentIndex = index;
    this.#updateNavState();
    this.#syncViewportHeight();
    if (this.trackEl) {
      this.trackEl.style.transform = `translateX(-${index * 100}%)`;
    }
  }

  #updateNavState() {
    if (this.prevBtn) {
      const atStart = this.#currentIndex <= 0;
      this.prevBtn.disabled = atStart;
      this.prevBtn.setAttribute('aria-disabled', String(atStart));
    }
    if (this.nextBtn) {
      const atEnd = this.#currentIndex >= this.#slides.length - 1;
      this.nextBtn.disabled = atEnd;
      this.nextBtn.setAttribute('aria-disabled', String(atEnd));
    }
  }

  #buildSearchUrl(sku) {
    const url = new URL(window.Theme?.routes?.search_url || '/search', window.location.origin);
    url.searchParams.set('q', `variants.sku:${sku}`);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[fields]', 'variants.sku');
    url.searchParams.set('section_id', RESULTS_SECTION_ID);
    return url;
  }

  async #getSkuResult(sku) {
    const cached = this.#skuCache.get(sku);
    if (cached) return cached;

    const url = this.#buildSearchUrl(sku);
    const html = await sectionRenderer.getSectionHTML(RESULTS_SECTION_ID, false, url);

    const container = document.createElement('div');
    container.innerHTML = html || '';
    const resultsEl = container.querySelector('[data-shop-the-look-results]');
    const visibleCount = resultsEl ? Number(resultsEl.dataset.visibleCount || 0) : 0;
    const result = { visibleCount, html: resultsEl ? resultsEl.innerHTML : '' };

    this.#skuCache.set(sku, result);
    return result;
  }

  async #getLookResults(look) {
    const perSku = await Promise.all(
      look.skus.map((sku) =>
        this.#getSkuResult(sku).catch((error) => {
          console.error('Shop The Look: failed to load SKU', sku, error);
          return { visibleCount: 0, html: '' };
        })
      )
    );

    return {
      visibleCount: perSku.reduce((total, entry) => total + entry.visibleCount, 0),
      html: perSku.map((entry) => entry.html).join(''),
    };
  }
}

if (!customElements.get('shop-the-look-pdp')) {
  customElements.define('shop-the-look-pdp', ShopTheLookPdp);
}
